import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AuthContext = createContext();

// Storage keys
const AUTH_STORAGE_KEY = 'vindicated_auth_v1';
const ENTITLEMENTS_STORAGE_KEY = 'vindicated_entitlements_v1';

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [entitlements, setEntitlements] = useState({
    has_full_journey_access: false,
    free_compare_reports_total: 3,
    free_compare_reports_used: 0,
    max_compare_reports_per_year: 100,
    annual_compare_reports_used: 0,
    contract_scans_included_total: 3,
    contract_scans_used: 0
  });

  // Load auth state from localStorage on mount
  useEffect(() => {
    const loadAuthState = () => {
      try {
        const savedAuth = localStorage.getItem(AUTH_STORAGE_KEY);
        const savedEntitlements = localStorage.getItem(ENTITLEMENTS_STORAGE_KEY);
        
        if (savedAuth) {
          const parsed = JSON.parse(savedAuth);
          setUser(parsed.user);
          setIsAuthenticated(true);
        }
        
        if (savedEntitlements) {
          const parsed = JSON.parse(savedEntitlements);
          setEntitlements(parsed);
        }
      } catch (e) {
        console.error('Failed to load auth state:', e);
      } finally {
        setIsLoading(false);
      }
    };
    
    loadAuthState();
  }, []);

  // Save auth state to localStorage
  const saveAuthState = useCallback((userData) => {
    try {
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify({ user: userData }));
    } catch (e) {
      console.error('Failed to save auth state:', e);
    }
  }, []);

  // Save entitlements to localStorage
  const saveEntitlements = useCallback((data) => {
    try {
      localStorage.setItem(ENTITLEMENTS_STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      console.error('Failed to save entitlements:', e);
    }
  }, []);

  // Start magic link sign in
  const startSignIn = async (email) => {
    try {
      const response = await axios.post(`${API}/auth/start`, { email });
      return { success: true, data: response.data };
    } catch (error) {
      console.error('Sign in start failed:', error);
      return { success: false, error: error.response?.data?.detail || 'Failed to send magic link' };
    }
  };

  // Complete magic link sign in
  const completeSignIn = async (token) => {
    try {
      const response = await axios.post(`${API}/auth/complete`, { token });
      const userData = response.data.user;
      
      setUser(userData);
      setIsAuthenticated(true);
      saveAuthState(userData);
      
      // Fetch entitlements after sign in
      await fetchEntitlements();
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('Sign in complete failed:', error);
      return { success: false, error: error.response?.data?.detail || 'Failed to complete sign in' };
    }
  };

  // Fetch entitlements from API
  const fetchEntitlements = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/entitlements`);
      const data = response.data;
      setEntitlements(data);
      saveEntitlements(data);
      return data;
    } catch (error) {
      console.error('Failed to fetch entitlements:', error);
      // Return cached entitlements on error
      return entitlements;
    }
  }, [entitlements, saveEntitlements]);

  // Sign out
  const signOut = useCallback(() => {
    setUser(null);
    setIsAuthenticated(false);
    setEntitlements({
      has_full_journey_access: false,
      free_compare_reports_total: 3,
      free_compare_reports_used: 0,
      max_compare_reports_per_year: 100,
      annual_compare_reports_used: 0,
      contract_scans_included_total: 3,
      contract_scans_used: 0
    });
    localStorage.removeItem(AUTH_STORAGE_KEY);
    localStorage.removeItem(ENTITLEMENTS_STORAGE_KEY);
  }, []);

  // Start Stripe checkout
  const startCheckout = async () => {
    try {
      const response = await axios.post(`${API}/start-checkout`);
      return { success: true, url: response.data.url };
    } catch (error) {
      console.error('Checkout start failed:', error);
      return { success: false, error: error.response?.data?.detail || 'Failed to start checkout' };
    }
  };

  // Check remaining compare reports
  const getRemainingCompareReports = useCallback(() => {
    if (entitlements.has_full_journey_access) {
      return {
        remaining: entitlements.max_compare_reports_per_year - entitlements.annual_compare_reports_used,
        total: entitlements.max_compare_reports_per_year,
        isPaid: true
      };
    }
    return {
      remaining: entitlements.free_compare_reports_total - entitlements.free_compare_reports_used,
      total: entitlements.free_compare_reports_total,
      isPaid: false
    };
  }, [entitlements]);

  // Check if user can use compare feature
  const canUseCompare = useCallback(() => {
    const { remaining } = getRemainingCompareReports();
    return remaining > 0;
  }, [getRemainingCompareReports]);

  // Check remaining contract scans
  const getRemainingScans = useCallback(() => {
    return {
      remaining: entitlements.contract_scans_included_total - entitlements.contract_scans_used,
      total: entitlements.contract_scans_included_total
    };
  }, [entitlements]);

  // Check if user can use scan feature
  const canUseScan = useCallback(() => {
    if (!entitlements.has_full_journey_access) return false;
    const { remaining } = getRemainingScans();
    return remaining > 0;
  }, [entitlements, getRemainingScans]);

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated,
      isLoading,
      entitlements,
      startSignIn,
      completeSignIn,
      signOut,
      fetchEntitlements,
      startCheckout,
      getRemainingCompareReports,
      canUseCompare,
      getRemainingScans,
      canUseScan
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
